import React, {Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useState} from "react";
import {useModal, useModalControls} from "@traas/lib/src/components/layout/ModalProvider";
import {Workorder} from "../../../lib/src/api/dto/Workorder";
import {QuantityType} from "../../../lib/src/api/dto/QuantityType"
import {Team} from "../../../lib/src/api/dto/Team"
import {Task, TaskStatus} from "../../../lib/src/api/dto/Task";
import {useApiCall} from "../api/api";
import {useApi} from "../api/APIContext";
import {DataTable} from "@traas/lib/src/components/data/DataTable";
import moment from "moment";
import {useNavigate} from "react-router";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faCheckSquare,
  faChevronDown,
  faChevronUp,
  faCircleQuestion,
  faClipboardCheck,
  faEnvelope,
  faFileExport,
  faImage,
  faNoteSticky,
  faPencil,
  faSquareCheck
} from "@fortawesome/free-solid-svg-icons";
import {faSquare} from "@fortawesome/pro-light-svg-icons";
import {TaskContextMenu} from "../planning/components/TaskContextMenu";
import {useThemes} from "../planning/util/theming";
import {useTaskTypes} from "../planning/hooks/useTaskTypes";
import {WorkorderDetailsModal} from "./WorkorderDetailsModal";
import {Button} from "@traas/lib/src/components/form/Button";
import {Project} from "../../../lib/src/api/dto/Project";
import {getWeek} from "@traas/lib/src/util/getWeekNumber";

export const ProjectDetailsModal: FC<{project: Project, goToDay: (day: Date) => void, focusTask: (task: Task) => void}> = (props) => {
  return <form>
    <div>
      {/* Information*/}
      <ProjectOverview project={props.project}/>
      {/* Tabs */}
      <ProjectTabs project={props.project} focusTask={props.focusTask} goToDay={props.goToDay}/>
    </div>
  </form>
}
const ProjectOverview = ({project}: { project: Project }) => {
  const {tasks} = useApi()
  const projectTasks = tasks.filter(t => t.projectId === project.id)
  return <div className={'grid grid-cols-1 lg:grid-cols-2 gap-4'}>
    <div>
      <div className={'text-lg font-bold mb-2'}>Projectgegevens</div>
      <div className={'border border-slate-200 px-4 py-3 rounded-lg grid grid-cols-3'}>
        <div className={'font-medium text-slate-800'}>Nr.:</div>
        <div className={'col-span-2'}>{project.projectNumber}</div>
        <div className={'font-medium text-slate-800'}>Omschrijving:</div>
        <div className={'col-span-2'}>{project.description}</div>
        <div className={'col-span-3'}>
          {projectTasks.length > 0 && <>
            <ProjectRemarkEditor task={projectTasks[0]} />
          </>}
        </div>
      </div>
    </div>
    <div>
      <div className={'text-lg font-bold mb-2'}>Aflever informatie</div>
      <div className={'border border-slate-200 px-4 py-3 rounded-lg grid grid-cols-3'}>
        <div className={'font-medium text-slate-800'}>Naam:</div>
        <div className={'col-span-2'}>{project.projectNumber}</div>
        <div className={'font-medium text-slate-800'}>E-mail:</div>
        <div className={'col-span-2 underline'}><a href={`mailto:${project.deliveryEmail}`}>{project.deliveryEmail}</a> <FontAwesomeIcon className={'ml-2'} icon={faEnvelope}/></div>
        <div className={'font-medium text-slate-800'}>Telefoon:</div>
        <div className={'col-span-2'}>{project.deliveryPhone}</div>
        <div className={'font-medium text-slate-800'}>Adres:</div>
        <div className={'col-span-2'}>
          <div>{project.deliveryAddressStreetname} {project.deliveryAddressHouseNumber}{project.deliveryAddressHouseNumberAddition}</div>
          <div>{project.deliveryZipCode} {project.deliveryCity}</div>
        </div>
      </div>
    </div>
  </div>
}

const ProjectRemarkEditor: FC<{task: Task}> = ({task}) => {
  const {updateTaskMetadata} = useApi()
  return <NoteEditor label={'Opmerkingen op Project'} text={task.projectRemark} isImportant={task.isProjectRemarkImportant} saveText={async (newText, isImportant) => {
    await updateTaskMetadata(task.id, {projectRemark: newText, is_project_remark_important: isImportant})
  }} />
}

const ProjectTabs = ({project, goToDay, focusTask}: {project: Project, goToDay: (day: Date) => void, focusTask: (day: Task) => void}) => {
  const {workorders, tasks} = useApi()
  const projectTasks = tasks.filter(t => t.projectId === project.id)
  const projectWorkorders = workorders.filter(w => w.projectNumber === project.projectNumber)
  const tabs = [
    {name: `Taken (${projectTasks.length})`, content: <ProjectTasksTab project={project} tasks={projectTasks} focusTask={focusTask} />},
    {name: `Werkbonnen (${projectWorkorders.length})`, content: <ProjectWorkordersTab project={project} workorders={projectWorkorders} goToDay={goToDay} tasks={projectTasks} />},
  ]
  const [tab, setTab] = React.useState<number>(0)
  return <div>
    <div className={'-mx-4 border-b-[3px] border-slate-300 flex px-4'}>
      {tabs.map((t, i) => <button type={"button"} className={`rounded-t h-12 font-medium text-sm uppercase px-4 border-b-[3px] ${i === tab ? 'text-brand-800 bg-brand-50 border-brand-800' : 'text-slate-800 border-slate-300 hover:text-brand-800'} mb-[-3px]`} key={i} onClick={() => setTab(i)}>{t.name}</button>)}
    </div>
    <div className={'h-[50vh] overflow-y-scroll -mx-4'}>
      <div className={'my-4 mx-4'}>
        {tabs[tab].content}
      </div>
    </div>
  </div>
}

const ProjectTasksTab = (props: {project: Project, tasks: Task[], focusTask: (day: Task) => void}) => {
  const {teams, quantityTypes} = useApi()
  const {getTaskName} = useTaskTypes()
  const navigate = useNavigate()
  const {close} = useModalControls()
  const sortedTasks = [...props.tasks].sort((a, b) => {
    if (!a.startAt && !b.startAt) return 0;
    if (!a.startAt) return 1;
    if (!b.startAt) return -1;
    return new Date(a.startAt).getTime() - new Date(b.startAt).getTime();
  });

  return <div className={''}>
    <div className={'mb-3 flex justify-between items-center border rounded-sm border-slate-200 px-3 py-2'}>
      <div>
        <h2 className={'font-medium mb-2'}>Taken kopieëren als tekst</h2>
        <p className={'text-sm text-slate-800'}>
          Kopieëer de onderstaande ingeplande taken naar je klembord om deze te delen met klanten.
        </p>
      </div>
      <Button type={'primary'} size={'md'} text={'Kopieëren als tekst'} icon={faClipboardCheck} onClick={() => {
        const text = sortedTasks.filter(t => t.startAt !== null).map(t => {
          return `${moment(t.startAt).format('DD-MM-YYYY')}: \t${getTaskName(t)}`
        }).join(`\r\n`)
        navigator.clipboard.writeText(text)
        return new Promise(resolve => setTimeout(resolve, 100))
      }} />
    </div>
    <DataTable keyProperty={'id'} data={sortedTasks} compact={true} placeholder={'Geen taken'} columns={[
      {header: 'Hoeveelheid', property: 'quantityAmount', transform: (_, t) => {
          if (t.quantityAmount !== null && t.quantityType !== null) {
            const type = quantityTypes.find(qt => qt.id === t.quantityType)
            if (type?.hideArea) {
              return type.name
            }
            return `${t.quantityAmount}m² ${type?.name ?? t.quantityType}`;
          } else {
            return '';
          }
        }},
      {header: 'Omschrijving', property: 'description'},
      {header: 'Gepland', property: 'startAt', transform: (v: Date|null) => <span className={v && moment(v).isBefore(new Date) ? 'text-slate-500' : 'font-medium'}>{v !== null ? moment(v).format('DD-MM-YYYY') : '-'}</span>},
      {header: 'Team', property: 'teamId', transform: (v: string|null) => teams.find(t => t.id === v)?.name ?? ''},
    ]} onClickRow={(row) => {
      if (row.startAt !== null) {
        props.focusTask(row)
        navigate('/')
        close()
      }
    }} />
  </div>
}

const ProjectWorkordersTab = (props: {project: Project, workorders: Workorder[], goToDay: (day: Date) => void, tasks: Task[]}) => {
  const {teams, quantityTypes} = useApi()
  const {downloadBatchWorkorderPdf} = useApiCall()
  const sortedWorkorders = [...props.workorders].sort((a, b) => {
    if (!a.startAt && !b.startAt) return 0;
    if (!a.startAt) return 1;
    if (!b.startAt) return -1;
    return new Date(a.startAt).getTime() - new Date(b.startAt).getTime();
  });

  const workorderMap = props.workorders.map((w) => w.workorderId!)
  const [checkedItems, setCheckedItems] = useState<string[]>([])

  const downloadReport = async () => {
    const blob = await downloadBatchWorkorderPdf(checkedItems)
    const file = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = file
    a.download = `project_${props.project.projectNumber}_werkbonnen.zip`
    a.click()
    URL.revokeObjectURL(file)
  }

  const selectAll = () => {
    setCheckedItems(workorderMap);
  };


  const unselectAll = () => {
    setCheckedItems([]);
  };

  return <>

    <div className={"flex py-3 space-x-3"}>
      <Button type={'secondary'} size={'sm'} icon={faCheckSquare} text={'Alle'} onClick={selectAll}/>
      <Button type={'secondary'} size={'sm'} icon={faSquare} text={'Geen'} onClick={unselectAll}/>
      {checkedItems.length > 0 && <>
        <Button type={'primary'} size={'sm'}
                text={checkedItems.length >= 2 ? `Exporteer werkbonnen (${checkedItems.length})` : `Exporteer werkbon`}
                icon={faFileExport}
                onClick={downloadReport}/>
      </>}
    </div>

    <div className={"border-2 border-slate-200 rounded-sm"}>
      <table className={'w-full'}>
        <tbody>
        <tr className={'text-left border-b-2 border-slate-100'}>
          <th></th>
          <th className={'pl-2'}>Hoeveelheid</th>
          <th>Omschrijving</th>
          <th>Status</th>
          <th>Gepland</th>
          <th>Team</th>
          <th></th>
        </tr>
        {sortedWorkorders.map((workorder, index) => (
          <CustomWorkOrderTableRow
            key={index}
            workorder={workorder} isFirst={index === 0}
            isLast={sortedWorkorders.length - 1 === index}
            quantityTypes={quantityTypes}
            project={props.project}
            teams={teams}
            tasks={props.tasks}
            state={checkedItems}
            stateSetter={setCheckedItems}
          />
        ))}
        </tbody>
      </table>
    </div>
  </>
}

const CustomWorkOrderTableRow: FC<{
  workorder: Workorder,
  isFirst: boolean,
  isLast: boolean,
  quantityTypes: QuantityType[],
  project: Project,
  teams: Team[],
  tasks: Task[],
  state: string[],
  stateSetter: Dispatch<SetStateAction<string[]>>
}> = (props) => {
  const task = props.tasks.find((task) => task.id === props.workorder.id)
  const detailsModal = useModal({title: "Werkbon", size: 'xl', body: <WorkorderDetailsModal order={props.workorder!} task={task!}/>})
  const [showDetails, setShowDetails] = useState<boolean>(false)
  const {getTheme} = useThemes()
  const {getTaskName, getTaskColor} = useTaskTypes()
  const theme = task ? getTheme(getTaskColor(task) ?? undefined, task.status === TaskStatus.invoiced) : getTheme()
  let imgCount = 0;
  let questionCount = 0;
  props.workorder.feedback?.forEach((feedback) => {
    if (feedback.answerType === 'image') {
      imgCount++;
    } else {
      questionCount++;
    }
  })
  const updateSelection = useCallback((key: string, newValue: boolean)  => {
    const stateWithoutKey = props.state.filter(k => k !== key)
    if (newValue) {
      props.stateSetter([key, ...stateWithoutKey])
    } else {
      props.stateSetter([...stateWithoutKey])
    }
  }, [props.state, props.stateSetter])
  const isChecked = useMemo(() => props.state.includes(props.workorder.workorderId!), [props.state, props.workorder])
  return <>
    <tr className={`${props.isLast ? '' : 'border-b-2'} border-slate-100 hover:bg-slate-50 cursor-pointer`} onClick={() => setShowDetails(!showDetails)}>
      <td className={'h-10 pl-2'}>
        <button type={"button"} className={'cursor-pointer h-8 w-8 hover:bg-brand-100 rounded-sm flex items-center justify-center'} onClick={(e) => {
          e.stopPropagation()
          updateSelection(props.workorder.workorderId!, !isChecked)
        }}>
          <FontAwesomeIcon icon={isChecked ? faCheckSquare : faSquare} className={isChecked ? 'text-brand-950' : 'text-slate-200'} />
        </button>
      </td>
      <td className={'pl-2'}>
        {task ? getTaskName(task) : '?'}
      </td>
      <td>{props.workorder.description}</td>
      <td>
        {props.workorder.isFinished ? (
          <div className="flex">
            <div className="px-2 h-5 text-xs rounded-full text-green-700 bg-green-100 font-medium uppercase flex items-center justify-start">
              <div className="h-2 w-2 rounded-full bg-green-600 mr-2"></div>
              <span>Afgerond</span>
            </div>
          </div>
        ) : (
          <div className="flex">
            <div className="px-2 h-5 text-xs rounded-full text-amber-700 bg-amber-100 font-medium uppercase flex items-center justify-start">
              <div className="h-2 w-2 rounded-full bg-amber-600 mr-2"></div>
              <span>Verstuurd</span>
            </div>
          </div>
        )}
      </td>
      <td>
        <span className={props.workorder.startAt && moment(props.workorder.startAt).isBefore(new Date) ? 'text-slate-500' : 'font-medium'}>
          {props.workorder.startAt !== null ? moment(props.workorder.startAt).format('DD-MM-YYYY') : '-'}
        </span>
      </td>
      <td>{props.teams.find((team) => team.id === props.workorder.teamId)?.name}</td>
      <td><FontAwesomeIcon icon={showDetails ? faChevronUp : faChevronDown} /></td>
    </tr>

  {showDetails && <tr className={'border-b-2 border-slate-100 bg-slate-50'}>
    <td colSpan={6} className={"py-2 px-3"}>
    <div className={'grid grid-cols-12 gap-4'}>
      <div className={'col-span-4'}>
        {task && <div className={`${theme.bg} rounded-sm text-white text-sm hover:cursor-pointer`} onClick={() => detailsModal.open()}>
          <TaskContextMenu task={task} onLeftMouseDown={() => {
          }} className={'flex space-x-3'}>
            <>
              <button className={`w-8 -mr-2 flex flex-col items-center justify-center hover:bg-[#0002]`}>
              <span
                className={"h-3 w-4 flex items-center justify-center text-[11px] text-[#fffd]"}>{moment(task.startAt!).year()}</span>
                <span
                  className={"h-3 flex items-center justify-center text-[12px] font-medium text-white"}><span
                  className={"mr-[2px] text-[#fffa]"}>w</span>{getWeek(task.startAt!)}</span>
              </button>
            </>
            <div className={`flex-1 text-left py-2 px-3`}>
              {task.status === TaskStatus.invoiced && <span className={"font-bold text-white"}>&euro;&nbsp;</span>}{getTaskName(task)}
              {props.project && <p
                className={`${theme.lightText} text-xs text-ellipsis -mx-1 px-1 ${theme.bg} rounded-sm`}>{task.status === TaskStatus.invoiced &&
                <span
                  className={`font-bold ${theme.text}`}>&euro;&nbsp;</span>}#{props.project.projectNumber}, {props.project.deliveryCity} - {props.project.description}</p>}
            </div>
          </TaskContextMenu>
        </div>}

      </div>

      <div className={'col-span-2'}>
        <div className={'h-16 border border-slate-200 px-3 py-2 rounded-sm flex flex-col justify-center'}>
          <div className={'flex justify-center items-center'}>
            <FontAwesomeIcon icon={faImage} className={'text-slate-500 mr-2'}/>
            <span className={'font-medium text-lg text-right'}>{imgCount}x</span>
          </div>
          <h1 className={'font-medium text-center text-sm'}>Afbeeldingen</h1>
        </div>
      </div>

      <div className={'col-span-2'}>
        <div className={'h-16 border border-slate-200 px-3 py-2 rounded-sm flex flex-col justify-center'}>
          <div className={'flex justify-center items-center'}>
            <FontAwesomeIcon icon={faCircleQuestion} className={'text-slate-500 mr-2'}/>
            <span className={'font-medium text-lg text-right'}>{questionCount}x</span>
          </div>
          <h1 className={'font-medium text-center text-sm'}>Ingevuld</h1>
        </div>
      </div>

      <div className={'col-span-4 border border-slate-200 px-2 py-1 text-sm rounded-sm'}>
        <h1 className={'font-medium mb-1'}>Opmerking</h1>
        <pre className={'text-sm font-sans text-slate-500 px-2 py-1 rounded-sm bg-slate-100 leading-tight whitespace-pre-wrap'}>{props.workorder.workorderRemark}</pre>
      </div>
    </div>
  </td>
  </tr>}
    </>
}

const NoteEditor: FC<{text?: string|null, isImportant?: boolean, saveText: (newText: string, isImportant: boolean) => Promise<void>, label: string}> = props => {
  const [mode, setMode] = useState<"view"|"edit">("view")
  const [isImportant, setIsImportant] = useState<boolean>(props.isImportant ?? false)
  const [viewText, setViewText] = useState(props.text ?? '')
  const [text, setText] = useState(props.text ?? '')
  const edit = () => {
    setText(props.text ?? '')
    setMode("edit")
  }
  const save = async () => {
    await props.saveText(text, isImportant)
    setViewText(text)
    setMode("view")
  }
  useEffect(() => {
    setViewText(props.text ?? '')
  }, [props.text]);
  return <div className={"border border-slate-200 rounded-sm p-2 my-2 w-full"}>
    <div className={"flex items-center justify-between"}>
      <span className={"flex items-center text-xs font-bold text-brand-900"}>
        <FontAwesomeIcon icon={faNoteSticky} className={"text-brand-900 text-xs mr-2"} />
        {props.label}
      </span>
    </div>

    {mode === "view" && <button type={'button'} className={"text-xs text-slate-600 mt-2 p-2 rounded-sm flex w-full hover:bg-slate-50"} onClick={() => edit()}>
      <pre className={'flex-1 text-left font-sans whitespace-pre-wrap'}>{viewText}</pre>
      <FontAwesomeIcon icon={faPencil} className={"text-slate-600 text-xs mr-2"} />
    </button>}
    {mode === "edit" && <div>
      <textarea className={"outline-hidden bg-slate-100 rounded-sm mt-2 mb-1 p-2 text-sm w-full"} value={text} onChange={(e) => setText(e.target.value)} />
      <div className={'flex flex-row items-center mb-2 px-3 space-x-2 rounded-sm hover:cursor-pointer hover:bg-brand-100'} onClick={() => setIsImportant(!isImportant)}>
        <FontAwesomeIcon icon={isImportant ? faSquareCheck : faSquare} className={`text-lg ${isImportant ? 'text-brand-900' : ''}`} />
        <div className={'py-1 px-1'}>
          <div className={'text-sm'}>Belangrijk</div>
          <div className={'text-xs'}>Hierbij legt de App extra nadruk op de ingevoerde notities</div>
        </div>
      </div>
      <div className={"flex space-x-2"}>
        <Button type={'primary'} size={'xs'} text={'Opslaan'} onClick={() => save()} />
        <Button type={'secondary'} size={'xs'} text={'Annuleren'} onClick={() => setMode("view")} />
      </div>
    </div>}
  </div>
}